// Remembers the last FULLY-RENDERED snapshot of each route, keyed by pathname. Used only as a fallback for
// the outgoing pane: in rapid back-and-forth the page being left can still be re-suspending (blank) at the
// instant we snapshot it, and there's no live content to capture. Rather than slide an empty box we reuse
// the last good snapshot of that route so the outgoing pane shows real content. Capped — these hold detached
// DOM subtrees.

import { matchesFrom } from './transitions.config'
import { LAYER_STYLE } from './snapshot'

/** Two is exactly the ping-pong the fallback exists for (A→B→A before A re-renders); each extra entry is a
 *  whole detached page held for a path that fires almost never, and there are two scopes' worth of them. */
const MAX_ENTRIES = 2
/**
 * Keyed by SURFACE then pathname. Nested transition surfaces render different slices of the same URL — on
 * /admin/overview the root surface's live pane is the whole admin console, while the tab surface's is just the
 * Overview panel — so one cache keyed on pathname alone would have them clobbering each other's entry and
 * handing back the wrong-sized pane.
 */
const caches = new Map<string, Map<string, HTMLElement>>()

/**
 * Layers handed to `rememberSnapshot` whose banking clone hasn't run yet. The clone is a full-page
 * `cloneNode(true)` and `rememberSnapshot` is called on the very frame that must paint a link slide's first
 * movement — so the clone is deferred off that frame and only ever paid in the background (or, for the rare
 * recall racing ahead of the flush, right when it's actually needed — see `flushPending`). Holding the LIVE
 * layer reference is safe: a snapshot's children never mutate, and only the layer's own inline styles do
 * (the controller's transforms), which the flush resets to the canonical layer style.
 */
const pending = new Map<string, { scope: string; pathname: string; layer: HTMLElement }>()
let flushScheduled = false

interface PostTaskScheduler {
  postTask?: (callback: () => void, options?: { priority?: 'background' }) => unknown
}

function scheduleFlush(): void {
  if (flushScheduled) return
  flushScheduled = true
  const run = () => {
    flushScheduled = false
    flushPending()
  }
  // Background priority — this feeds a fallback, and the whole point is to stay off the animation's frames.
  // setTimeout fallback for the old browsers this app still ships to (same constraint as defer-commit.ts).
  const scheduler = (globalThis as { scheduler?: PostTaskScheduler }).scheduler
  if (scheduler && typeof scheduler.postTask === 'function') {
    void Promise.resolve(scheduler.postTask(run, { priority: 'background' })).catch(() => {})
  } else {
    setTimeout(run, 0)
  }
}

function flushPending(): void {
  for (const { scope, pathname, layer } of pending.values()) {
    const cache = cacheFor(scope)
    cache.delete(pathname) // re-insert so it becomes the most-recently-used (Map preserves insertion order)
    const clone = layer.cloneNode(true) as HTMLElement
    // The controller may have written onto the layer since it was handed over (transform, z-index, …) —
    // reset to the canonical layer style so a recalled copy always mounts clean.
    clone.style.cssText = LAYER_STYLE
    cache.set(pathname, clone)
    while (cache.size > MAX_ENTRIES) {
      const oldest = cache.keys().next().value
      if (oldest === undefined) break
      cache.delete(oldest)
    }
  }
  pending.clear()
}

function cacheFor(scope: string): Map<string, HTMLElement> {
  const existing = caches.get(scope)
  if (existing) return existing
  const created = new Map<string, HTMLElement>()
  caches.set(scope, created)
  return created
}

/** Bank `layer` (a captured snapshot's node) as the last-good render of `pathname`. The deep clone this
 *  requires is deferred off the caller's frame — see `pending`. */
export function rememberSnapshot(scope: string, pathname: string, layer: HTMLElement): void {
  pending.set(`${scope} ${pathname}`, { scope, pathname, layer })
  scheduleFlush()
}

/**
 * A fresh, ready-to-mount snapshot of the last-good render of `pathname`, or `null` if we have none.
 *
 * Matches by prefix, most-recent first: a transition targets a section root (`/admin`) but what actually
 * rendered — and what we cached — is where it landed after any redirect (`/admin/overview`). An exact-key
 * lookup would always miss and fall back to the skeleton.
 */
export function recallSnapshot(
  scope: string,
  pathname: string,
): { layer: HTMLElement; applyScroll: () => void } | null {
  // A same-tick remember→recall (programmatic rapid back-and-forth) must still hit — settle any deferred
  // banking first. Recall only runs on the rare BLANK path, so this stays off the common frame.
  if (pending.size > 0) flushPending()
  const cache = cacheFor(scope)
  let hit = cache.get(pathname)
  if (!hit) {
    // Map iterates oldest→newest, so keep overwriting to end up with the most recent prefix match.
    for (const [key, layer] of cache) {
      if (matchesFrom(key, pathname)) hit = layer
    }
  }
  if (!hit) return null
  return { layer: hit.cloneNode(true) as HTMLElement, applyScroll: () => {} }
}
